import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, session } from '../../shell/bridge';
import { Composer } from '../../components/Composer';
import { Tooltip } from '../../ui/Tooltip';
import './share.css';

/**
 * Us → "Something to share" (card a5fbGvPIggaR, founder email 2026-10-21).
 *
 * The prompt this replaces asked for a number on a 1-10 scale. This asks for a
 * sentence about the relationship, and gives it two doors: WHO MAY
 * SEE it (only me, default — the same privacy-first default the gifting brief
 * ships with) and whether it goes on the weekly meeting's agenda.
 *
 * The privacy gate is structural, not cosmetic: while the share is just-me the
 * "Let's talk about it" switch is off and inert with the reason beside it, so a
 * private row cannot be armed for the meeting in the first place. The server
 * clamps it again (a just-me share stores talk=0 no matter what the client
 * sent) and filters a partner's just-me rows out of every read, so the toggle
 * is a promise the backend keeps whether or not the UI held.
 */
interface UsShare {
  shareId: string;
  text: string;
  talk: boolean;
  audience: 'shared' | 'secret';
  createdBy: string;
  createdAt: number;
}

const MAX_SHARE = 600;

function ago(at: number): string {
  const days = Math.floor((Date.now() - at) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

export function SharePanel() {
  const me = session();

  const [shares, setShares] = useState<UsShare[] | null>(null);
  const [draft, setDraft] = useState('');
  const [audience, setAudience] = useState<'secret' | 'shared'>('secret');
  const [talk, setTalk] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api<{ shares: UsShare[] }>('/api/plan/shares');
      setShares(data.shares ?? []);
    } catch {
      /* the composer still works without the list — never block a share on it */
      setShares(prev => prev ?? []);
    }
  }, []);

  // Same key-on-id rule as the question panel: session() hands back a fresh
  // object every render, so the id pair is the only dependency that means
  // "a real sign-in happened".
  const meKey = me ? `${me.coupleId}:${me.memberId}` : '';
  useEffect(() => {
    if (meKey) void load();
  }, [meKey, load]);

  if (!me) return <p className="q-guard">Please log in first.</p>;

  const chooseAudience = (value: 'secret' | 'shared') => {
    setAudience(value);
    // Widening the audience never arms the agenda on its own; NARROWING it has
    // to disarm it — a row the partner may not read must not claim a seat at
    // their meeting.
    if (value === 'secret') setTalk(false);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await api('/api/plan/shares', { method: 'POST', body: { text, talk, audience } });
      setDraft('');
      setTalk(false);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could Not Save That Share');
    } finally {
      setSending(false);
    }
  };

  const myId = me.memberId;

  return (
    <div className="share-panel" data-testid="share-panel">
      {/* Founder email 2026-10-27: "this screen should follow the same rule ie
          overview goes in tooltip which is in wiki". The overview paragraph no
          longer sits on the surface — it is the tooltip beside the heading
          (the same pattern as Us > Questions and Life > Requests), and the
          long form of it is the `us-share` entry in the Help inventory
          (web/src/lib/help.ts). The heading also repeats the subtab name, the
          rule on card 78reeQdjrFAJ, so the tip has a title to hang off. */}
      <div className="us-heading-row">
        <h2
          className="section-title"
          style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 'var(--type-h3-size)',
            letterSpacing: 'var(--type-tracking-wider)',
            fontWeight: 400,
            color: 'var(--color-cream)',
            marginTop: 0,
          }}
        >
          Something to share
        </h2>
        <Tooltip label="Something about the two of you — a thing you want to say, or a thing you want to talk about. It stays yours until you widen who can see it; widen it and you can put it on the weekly meeting.">
          <span
            className="pear-tip-trigger"
            id="hint-share"
            data-tip-for="us-share"
            title="Something about the two of you — a thing you want to say, or a thing you want to talk about. It stays yours until you widen who can see it; widen it and you can put it on the weekly meeting."
          >
            ⓘ
          </span>
        </Tooltip>
      </div>

      <Composer
        id="share-input"
        formClassName="share-form"
        inputClassName="pear-chat-input share-input"
        label="Something to share"
        placeholder="Something about the two of you…"
        value={draft}
        onChange={setDraft}
        onSubmit={submit}
        maxLength={MAX_SHARE}
        dataTest="share-input"
        sendLabel={sending ? 'Saving…' : 'Share'}
        sendDisabled={!draft.trim() || sending}
        sendClassName="share-send"
        controlsClassName="share-controls"
        controls={
          <>
            {/* WHO MAY SEE — the same two-button vocabulary the gifting brief
                uses, so visibility reads identically wherever it appears. */}
            <div className="share-audience" role="radiogroup" aria-label="Who can see this share">
              <button
                type="button"
                role="radio"
                aria-checked={audience === 'secret'}
                className={`share-audience-btn${audience === 'secret' ? ' is-on' : ''}`}
                data-testid="share-audience-secret"
                onClick={() => chooseAudience('secret')}
              >
                Only me
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={audience === 'shared'}
                className={`share-audience-btn${audience === 'shared' ? ' is-on' : ''}`}
                data-testid="share-audience-shared"
                onClick={() => chooseAudience('shared')}
              >
                My partner too
              </button>
            </div>

            {/* THE AGENDA GATE — inert while the row is just-me, and it says
                why rather than silently refusing the tap. */}
            <div className={`share-talk${talk ? ' is-on' : ''}`}>
              <button
                type="button"
                role="switch"
                aria-checked={talk}
                aria-label="Let's talk about it"
                className="share-talk-btn"
                data-testid="share-talk"
                disabled={audience === 'secret'}
                onClick={() => setTalk(v => !v)}
              >
                <span className="share-talk-knob" />
              </button>
              <span className="share-talk-label">
                {audience === 'secret'
                  ? 'Let’s talk about it — switch to “My partner too” first'
                  : 'Let’s talk about it — put this on the weekly meeting'}
              </span>
            </div>
          </>
        }
      />

      {shares && shares.length > 0 && (
        <ul className="share-list" data-testid="share-list">
          {shares.map(s => (
            <li className="share-item" key={s.shareId} data-testid="share-item" data-share-id={s.shareId}>
              <p className="share-item-text" data-testid="share-item-text">{s.text}</p>
              <p className="share-item-meta">
                <span className="share-chip">
                  {s.createdBy === myId
                    ? s.audience === 'shared' ? 'you shared it' : 'only you'
                    : 'your partner shared it'}
                </span>
                {s.talk && s.audience === 'shared' && (
                  <span className="share-chip share-chip-agenda">on the meeting agenda</span>
                )}
                <span className="share-when">{ago(s.createdAt)}</span>
              </p>
            </li>
          ))}
        </ul>
      )}

      {shares && shares.length === 0 && (
        <p className="share-empty" data-testid="share-empty">
          Nothing shared yet.
        </p>
      )}
    </div>
  );
}
